import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, Bell, Clock, Mail, Globe, Cookie, BarChart3, Send } from 'lucide-react';

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_a85f478b-763e-4b7e-9f54-0dbb5a37a168/artifacts/fk4lgbat_Logo%20Phocea%20Rent.png";

const ConfidentialitePage = () => {
  const sections = [
    {
      icon: Database,
      title: "2. Données personnelles collectées",
      content: `Dans le cadre de l'utilisation du site et des services proposés, Phocea Rent® est amenée à collecter les données suivantes :

**2.1 Données d'identité et de contact**
- Nom, prénom
- Adresse email
- Numéro de téléphone
- Adresse postale
- Informations professionnelles (raison sociale, SIRET, fonction)

**2.2 Données liées à l'activité commerciale**
- Historique des commandes et locations
- Informations relatives aux contrats de location
- Suivi des prestations
- Échanges avec le service client

**2.3 Données de compte**
- Identifiants de connexion (mot de passe chiffré)
- Préférences de communication

**2.4 Données de navigation**
- Adresse IP
- Type de navigateur
- Pages visitées
- Données de géolocalisation approximative

**2.5 Données liées au matériel loué**
- Données techniques relatives à l'utilisation du matériel
- Données de localisation (si équipement installé)
- Données nécessaires à la sécurité des biens
* Ces dispositifs ne permettent aucune captation sonore ou visuelle.`
    },
    {
      icon: Eye,
      title: "3. Finalités de la collecte",
      content: `Les données personnelles sont collectées pour les finalités suivantes :

**3.1 Exécution du contrat**
- Création et gestion du compte client
- Gestion des devis, commandes et locations
- Livraison et récupération du matériel
- Suivi des locations
- Facturation et comptabilité
- Gestion des impayés

**3.2 Gestion du service client**
- Réponse aux demandes
- Gestion des réclamations
- Suivi des litiges

**3.3 Sécurité et protection**
- Prévention du vol ou de la perte de matériel
- Suivi technique du matériel
- Vérification du respect des conditions d'utilisation

**3.4 Amélioration des services**
- Analyse statistique
- Amélioration du site et des services

**3.5 Marketing (avec consentement)**
- Envoi d'offres commerciales
- Publicité personnalisée
- Recommandations de services`
    },
    {
      icon: Shield,
      title: "4. Base légale du traitement",
      content: `Les traitements sont fondés sur :

- **L'exécution du contrat**
- **Le respect des obligations légales**
- **Le consentement** (cookies, marketing)
- **L'intérêt légitime** de Phocea Rent®`
    },
    {
      icon: Clock,
      title: "5. Durée de conservation des données",
      content: `Les données sont conservées pendant :

- **Données clients** : durée du contrat + 3 ans
- **Données de facturation** : 10 ans
- **Données marketing** : 3 ans
- **Données techniques** : 12 mois
- **Cookies** : 13 mois maximum`
    },
    {
      icon: Send,
      title: "6. Destinataires des données",
      content: `Les données peuvent être transmises à :

- Prestataires de paiement (Stripe, Alma…)
- Transporteurs et services logistiques (ex : Dasher)
- Prestataires techniques (hébergement, CRM, emailing)
- Prestataires de maintenance

Les données peuvent également être transmises aux autorités compétentes en cas d'obligation légale.`
    },
    {
      icon: Globe,
      title: "7. Transfert hors Union Européenne",
      content: `Certains outils peuvent entraîner un transfert de données hors Union Européenne.

Dans ce cas, Phocea Rent® met en place les garanties nécessaires (clauses contractuelles types, etc.).`
    },
    {
      icon: Lock,
      title: "8. Sécurité des données",
      content: `Phocea Rent® met en œuvre toutes les mesures nécessaires pour garantir la sécurité des données personnelles contre tout accès non autorisé, perte ou divulgation.`
    },
    {
      icon: UserCheck,
      title: "9. Vos droits",
      content: `Conformément à la réglementation en vigueur, vous disposez des droits suivants :

- Droit d'accès
- Droit de rectification
- Droit de suppression
- Droit d'opposition
- Droit à la limitation
- Droit à la portabilité

**Vous pouvez exercer vos droits à tout moment :**
contact@phocearent.fr

Vous disposez également du droit d'introduire une réclamation auprès de la CNIL.`
    },
    {
      icon: Cookie,
      title: "10. Cookies",
      content: `Le site www.phocearent.fr utilise des cookies pour :

- Assurer le bon fonctionnement du site
- Mesurer l'audience
- Améliorer l'expérience utilisateur
- Proposer des contenus personnalisés

**Gestion des cookies**
Lors de votre première visite, un bandeau vous permet :
- d'accepter
- de refuser
- ou de personnaliser les cookies

Vous pouvez modifier vos préférences à tout moment via votre navigateur ou le bandeau cookies.`
    },
    {
      icon: BarChart3,
      title: "11. Données collectées automatiquement",
      content: `Lors de votre navigation, certaines données peuvent être collectées automatiquement :

- Adresse IP
- Pages consultées
- Durée de visite
- Type de navigateur

Ces données sont utilisées uniquement à des fins statistiques.`
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black/95 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_WHITE} alt="Phocea Rent" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-slate-400 hover:text-[#DFFF00] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au site
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-slate-900/50 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-full px-4 py-2 mb-6">
            <Shield className="w-4 h-4 text-[#DFFF00]" />
            <span className="text-[#DFFF00] text-sm font-medium">Protection des données</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Politique de gestion des données personnelles
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Phocea Rent® – KF DIFFUSION
          </p>
          <p className="text-slate-500 text-sm mt-4">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Responsable du traitement */}
          <article 
            data-testid="confidentialite-section-1"
            className="bg-slate-900/30 border border-slate-800 rounded-2xl overflow-hidden mb-8"
          >
            <div className="bg-slate-800/50 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#DFFF00]/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-[#DFFF00]" />
              </div>
              <h2 className="text-lg font-semibold text-white">1. Responsable du traitement</h2>
            </div>
            <div className="px-6 py-5 text-slate-400 leading-relaxed space-y-2">
              <p>Les données personnelles collectées sur le site www.phocearent.fr sont traitées par :</p>
              <div className="mt-3 bg-slate-800/30 border border-slate-700 rounded-xl p-4">
                <p className="text-white font-semibold">Phocea Rent® – KF DIFFUSION</p>
                <p>65 Avenue Jacqueline Auriol, Bâtiment 5</p>
                <p>13700 Marignane</p>
                <p>RCS Aix-en-Provence : 820 956 076</p>
                <p className="mt-2">
                  <a href="mailto:contact@phocearent.fr" className="text-[#DFFF00] hover:underline">contact@phocearent.fr</a>
                </p>
              </div>
            </div>
          </article>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <article 
                key={index}
                data-testid={`confidentialite-section-${index + 2}`}
                className="bg-slate-900/30 border border-slate-800 rounded-2xl overflow-hidden"
              >
                <div className="bg-slate-800/50 px-6 py-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#DFFF00]/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-[#DFFF00]" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">{section.title}</h2>
                </div>
                <div className="px-6 py-5">
                  <div 
                    className="text-slate-400 leading-relaxed whitespace-pre-line prose prose-invert prose-sm max-w-none
                      [&_strong]:text-white [&_strong]:font-semibold"
                    dangerouslySetInnerHTML={{ 
                      __html: section.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br/>')
                    }}
                  />
                </div>
              </article>
            ))}
          </div>

          {/* Contact Box */}
          <div className="mt-12 bg-gradient-to-r from-[#DFFF00]/10 to-transparent border border-[#DFFF00]/30 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-3">Questions sur vos données ?</h3>
            <p className="text-slate-400 mb-4">
              Pour toute question concernant la protection de vos données personnelles ou pour 
              exercer vos droits, contactez-nous.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="mailto:contact@phocearent.fr" 
                data-testid="confidentialite-contact-btn"
                className="inline-flex items-center gap-2 bg-[#DFFF00] text-black px-4 py-2 rounded-lg font-medium hover:bg-[#c8e600] transition-colors"
              >
                <Mail className="w-4 h-4" />
                contact@phocearent.fr
              </a>
              <Link 
                to="/cgv" 
                data-testid="confidentialite-cgv-link"
                className="inline-flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-700 transition-colors"
              >
                Conditions générales de location
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Mini */}
      <footer className="py-8 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
            <p>© {new Date().getFullYear()} Phocea Rent. Tous droits réservés.</p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="hover:text-[#DFFF00] transition-colors">
                Mentions légales
              </Link>
              <Link to="/cgv" className="hover:text-[#DFFF00] transition-colors">
                CGV
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConfidentialitePage;
